import { useEffect, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Download, Loader2, Wallet } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount || 0);

const formatDate = (value) =>
  value ? new Date(value).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

export default function StaffCollectionsPage() {
  const [summary, setSummary] = useState([]);
  const [pendingRows, setPendingRows] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    staff_id: 'none',
    amount: '',
    settlement_mode: 'cash',
    transaction_reference: '',
    notes: '',
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryRes, pendingRes, settlementsRes, staffRes] = await Promise.all([
        adminApi.getStaffCollectionSummary(),
        adminApi.getPendingStaffCollections(),
        adminApi.getStaffCollectionSettlements(),
        adminApi.getSalesStaff(),
      ]);
      setSummary(summaryRes.data || []);
      setPendingRows(pendingRes.data || []);
      setSettlements(settlementsRes.data || []);
      setStaff(staffRes.data || []);
    } catch (error) {
      toast.error('Failed to load staff collections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const totalPending = summary.reduce((sum, row) => sum + (row.pending_handover_amount || 0), 0);

  const downloadExcel = async (dataset) => {
    try {
      const res = await adminApi.downloadAdminDatasetExcel(dataset);
      downloadBlob(res.data, `${dataset}.xlsx`);
    } catch {
      toast.error('Failed to download Excel');
    }
  };

  const handleCreateSettlement = async () => {
    if (form.staff_id === 'none') {
      toast.error('Please select a staff member');
      return;
    }
    if (!(Number(form.amount) > 0)) {
      toast.error('Please enter a valid settlement amount');
      return;
    }
    setSaving(true);
    try {
      await adminApi.createStaffCollectionSettlement({
        staff_id: form.staff_id,
        amount: Number(form.amount),
        settlement_mode: form.settlement_mode,
        transaction_reference: form.transaction_reference || undefined,
        notes: form.notes || undefined,
      });
      toast.success('Settlement recorded');
      setShowModal(false);
      setForm({
        staff_id: 'none',
        amount: '',
        settlement_mode: 'cash',
        transaction_reference: '',
        notes: '',
      });
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to record settlement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="staff-collections-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Staff Collections</h1>
          <p className="text-[#A8A8A8] mt-1">Track customer payments collected by staff and settle handovers to company accounts</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadExcel('staff-summary')}>
            <Download className="w-4 h-4 mr-2" /> Summary Excel
          </Button>
          <Button variant="outline" onClick={() => downloadExcel('staff-pending')}>
            <Download className="w-4 h-4 mr-2" /> Pending Excel
          </Button>
          <Button variant="outline" onClick={() => downloadExcel('staff-settlements')}>
            <Download className="w-4 h-4 mr-2" /> Settlements Excel
          </Button>
          <Button className="bg-[#C97A5E] hover:bg-[#B06850]" onClick={() => setShowModal(true)}>
            Record Settlement
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Pending With Staff</p>
            <p className="text-2xl font-bold text-[#C97A5E]">{formatCurrency(totalPending)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Open Collections</p>
            <p className="text-2xl font-bold text-[#2D2420]">{pendingRows.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E5E5]">
          <CardContent className="p-4">
            <p className="text-sm text-[#A8A8A8]">Settlements Recorded</p>
            <p className="text-2xl font-bold text-[#4A7c59]">{settlements.length}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Staff Summary</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40"><Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Staff</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Pending Handover</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Collected Today</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Collected Month</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Settled Month</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {summary.map((row) => (
                    <tr key={row.staff_id}>
                      <td className="py-3 px-4 text-[#2D2420] font-medium">{row.staff_name}</td>
                      <td className="py-3 px-4 text-[#C97A5E] font-semibold">{formatCurrency(row.pending_handover_amount)}</td>
                      <td className="py-3 px-4 text-[#2D2420]">{formatCurrency(row.collected_today)}</td>
                      <td className="py-3 px-4 text-[#2D2420]">{formatCurrency(row.collected_month)}</td>
                      <td className="py-3 px-4 text-[#4A7c59]">{formatCurrency(row.settled_month)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Pending Handover Items</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Customer</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Staff</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Collected On</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Mode</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Pending</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {pendingRows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-3 px-4 text-[#2D2420]">{row.customer_name}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{row.collected_by_staff_name || '-'}</td>
                    <td className="py-3 px-4 text-[#A8A8A8]">{formatDate(row.customer_collection_date)}</td>
                    <td className="py-3 px-4 text-[#2D2420] uppercase">{row.customer_paid_mode || '-'}</td>
                    <td className="py-3 px-4 text-[#C97A5E] font-semibold">{formatCurrency(row.remaining_with_staff)}</td>
                  </tr>
                ))}
                {!pendingRows.length && !loading ? (
                  <tr><td colSpan={5} className="py-8 text-center text-[#A8A8A8]">No pending staff collections</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Recent Settlements</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Settlement No</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Staff</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Mode</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Amount</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {settlements.map((row) => (
                  <tr key={row.id}>
                    <td className="py-3 px-4 text-[#2D2420] font-medium">{row.settlement_no}</td>
                    <td className="py-3 px-4 text-[#2D2420]">{row.staff_name}</td>
                    <td className="py-3 px-4 text-[#2D2420] uppercase">{row.settlement_mode}</td>
                    <td className="py-3 px-4 text-[#4A7c59] font-semibold">{formatCurrency(row.amount)}</td>
                    <td className="py-3 px-4 text-[#A8A8A8]">{formatDate(row.settlement_date)}</td>
                  </tr>
                ))}
                {!settlements.length && !loading ? (
                  <tr><td colSpan={5} className="py-8 text-center text-[#A8A8A8]">No settlements yet</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Record Staff Settlement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[#A8A8A8]">Staff</label>
              <Select value={form.staff_id} onValueChange={(value) => setForm((prev) => ({ ...prev, staff_id: value }))}>
                <SelectTrigger className="w-full border-[#E5E5E5]">
                  <SelectValue placeholder="Select staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select staff</SelectItem>
                  {staff.map((member) => (
                    <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Input
              label="Amount"
              type="number"
              value={form.amount}
              onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
              placeholder="Enter amount"
            />
            <div>
              <label className="text-xs text-[#A8A8A8]">Settlement Mode</label>
              <Select value={form.settlement_mode} onValueChange={(value) => setForm((prev) => ({ ...prev, settlement_mode: value }))}>
                <SelectTrigger className="w-full border-[#E5E5E5]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank">Bank</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input
              label="Transaction Reference (Optional)"
              value={form.transaction_reference}
              onChange={(e) => setForm((prev) => ({ ...prev, transaction_reference: e.target.value }))}
              placeholder="UPI / bank reference"
            />
            <Input
              label="Notes (Optional)"
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="Add notes"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button className="bg-[#C97A5E] hover:bg-[#B06850]" onClick={handleCreateSettlement} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wallet className="w-4 h-4 mr-2" />}
                Save Settlement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
